import {type ARIAFilterSuggestion, type FilterProvider, FilterValueType} from '@github-ui/filter-core'
import {getDefaultValueVisualDescriptors, getFilterValue} from '@github-ui/filter-core'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {ActionList, CounterLabel} from '@primer/react'
import {clsx} from 'clsx'
import {memo, useCallback} from 'react'

import {useSuggestionsDispatch} from '../context'
import {type ResolvedValueVisual, resolveValueVisualDescriptor} from '../utils/value-visuals'
import styles from './SuggestionItem.module.css'

const countFormatter = new Intl.NumberFormat('en-US', {notation: 'compact', maximumFractionDigits: 1})

type SuggestionItemProps = {
  /** The index of the suggestion item, used for identifying which is active */
  suggestionId: number
  /** Whether or not the item should be displayed as active */
  active?: boolean
  /**
   * The provider resolved for this suggestion, used to render its leading visual. Resolved by the
   * parent (`SuggestionsList`) so this item does not need to subscribe to the filter query
   * context, which republishes on every keystroke.
   */
  provider?: FilterProvider
} & ARIAFilterSuggestion

export const SuggestionItem = memo(function SuggestionItemImpl(suggestion: SuggestionItemProps) {
  const {
    suggestionId,
    active,
    value,
    type,
    count,
    description,
    displayName,
    displayNameHtml,
    inlineDescription,
    ariaLabel,
    provider,
  } = suggestion
  const {suggestionSelected, activeSuggestionRef} = useSuggestionsDispatch()

  const renderValueVisual = type === FilterValueType.Key ? undefined : provider?.options.renderValueVisual
  const customLeadingVisual = renderValueVisual ? (renderValueVisual(suggestion) ?? null) : null
  const leadingVisualDescriptors = renderValueVisual
    ? customLeadingVisual
    : getDefaultValueVisualDescriptors(suggestion, provider?.icon)
  const resolvedLeadingVisual = resolveValueVisualDescriptor(leadingVisualDescriptors, provider?.icon)

  const onSuggestionSelectHandler = useCallback(() => {
    suggestionSelected(suggestion)
  }, [suggestion, suggestionSelected])

  const setActiveSuggestionRef = useCallback(
    (node: HTMLLIElement) => {
      if (active && node) {
        activeSuggestionRef.current = node
        node.scrollIntoView({block: 'nearest'})
      } else {
        activeSuggestionRef.current = null
      }
    },
    [active, activeSuggestionRef],
  )

  const displayNameClass = clsx(styles.displayName, description ? styles.boldText : styles.normalText)

  const renderSuggestionVisual = (visual: ResolvedValueVisual) => {
    if (visual.kind === 'avatar') {
      return (
        <GitHubAvatar
          src={visual.url}
          alt={visual.alt}
          square={visual.square ?? false}
          className={styles.GitHubAvatar_0}
        />
      )
    }

    if (visual.kind === 'color-swatch') {
      return <div style={{backgroundColor: visual.color}} className={styles.Box_1} />
    }

    const cssIconColor = visual.color ?? 'currentcolor'
    const VisualIcon = visual.icon
    return <VisualIcon fill={cssIconColor} />
  }

  return (
    <ActionList.Item
      id={`suggestion-${suggestionId}`}
      onSelect={onSuggestionSelectHandler}
      active={active}
      role="option"
      aria-label={ariaLabel}
      aria-selected={active}
      tabIndex={-1}
      aria-labelledby={undefined}
      ref={setActiveSuggestionRef}
      className={styles.item}
    >
      {resolvedLeadingVisual ? (
        <ActionList.LeadingVisual>
          <div className={styles.Box_0}>{renderSuggestionVisual(resolvedLeadingVisual)}</div>
        </ActionList.LeadingVisual>
      ) : null}
      {displayNameHtml !== undefined ? (
        <VerifiedHTMLText html={displayNameHtml} className={displayNameClass} />
      ) : (
        <span className={displayNameClass}>{displayName ?? getFilterValue(value) ?? ''}</span>
      )}
      {description && inlineDescription !== undefined && (
        <ActionList.Description
          variant={inlineDescription ? 'inline' : 'block'}
          className={styles.ActionList_Description}
          truncate={!!inlineDescription}
        >
          {description}
        </ActionList.Description>
      )}
      {count !== undefined && (
        <ActionList.TrailingVisual>
          <CounterLabel>{countFormatter.format(count)}</CounterLabel>
        </ActionList.TrailingVisual>
      )}
    </ActionList.Item>
  )
})
